import React, { useState, useEffect } from 'react';
import { Form, Input } from 'antd';
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from '@stripe/react-stripe-js';

import ValidationMessage from 'components/ValidationMessage';

import { defaultErrorMessage, contacts } from 'constants/global';
import axios from 'services/axios';
import StripeIcon from 'assets/icons/stripe.svg';

import { STRIPE_ELEMENT_OPTIONS } from './constants';
import { getStripeErrorMessage } from './helpers';
import './CardForm.scss';


const StripeError = class extends Error {
  constructor(message) {
    super(message);
    this.error = message;
  }
};


const CardForm = () => {
  const stripe = useStripe();
  const elements = useElements();

  const [cardNumberComplete, setCardNumberComplete] = useState(false);
  const [cardExpiryComplete, setCardExpiryComplete] = useState(false);
  const [cardCvcComplete, setCardCvcComplete] = useState(false);

  const [cardNumberError, setCardNumberError] = useState(false);
  const [cardExpiryError, setCardExpiryError] = useState(false);
  const [cardCvcError, setCardCvcError] = useState(false);

  const [isSubmitButtonDisabled, setSubmitButtonDisabled] = useState(true);

  const [isSucceeded, setIsSucceeded] = useState(false);
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [form] = Form.useForm();
  const name = Form.useWatch('name', form);
  const email = Form.useWatch('email', form);

  const onSubmit = async ({ name, email }) => {
    setIsLoading(true);
    setError(false);

    try {
      const {
        paymentMethod,
        error,
      } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardNumberElement),
        billing_details: {
          name,
          email,
        },
      });

      if (error !== undefined) {
        throw new StripeError(error.message);
      }

      const payResponse = await axios().post(process.env.REACT_APP_PAY_URL, {
        paymentMethodId: paymentMethod.id,
        billingDetails: paymentMethod.billing_details,
      });

      if (payResponse?.actionRequired) {
        // Use Stripe.js to handle required card action
        const actionHandled = await stripe.handleCardAction(
          payResponse.paymentIntentClientSecret,
        );

        if (actionHandled.error) {
          throw new StripeError(actionHandled.error.message);
        }
        await axios().post(process.env.REACT_APP_CONFIRM_PAYMENT_URL, {
          paymentIntentId: actionHandled.paymentIntent.id,
        });
      }

      setIsSucceeded(true);
    } catch (err) {
      setError(err.error || defaultErrorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(async () => {
    const isFormWithStripeLoaded = stripe && elements && !isLoading;
    const isCardDataComplete = cardNumberComplete && cardExpiryComplete && cardCvcComplete;
    const isFormFilled = Boolean(name) || Boolean(email);

    if (!isFormWithStripeLoaded || !isCardDataComplete || !isFormFilled) {
      setSubmitButtonDisabled(true);
      return;
    }

    setSubmitButtonDisabled(false);
  }, [
    stripe,
    elements,
    isLoading,
    cardNumberComplete,
    cardExpiryComplete,
    cardCvcComplete,
    name,
    email,
  ]);

  const renderStripeBlock = () => (
    <div className="stripe-block">
      <p>
        Guaranteed
        <strong>safe & secure</strong>
      </p>
      <img alt="stripe" src={StripeIcon} />
    </div>
  );

  const renderForm = () => (
    <>
      <h2>Credit Card Authorization</h2>
      <p className="note note-big">
        By submitting card details on this page you confirm that AltoVita has the authority to apply charges related
        to your booking and that you have read and agreed to the use of personal information that you are providing
        in accordance with the AltoVita
        {' '}
        {' '}
        <a target="_blank" rel="noopener noreferrer" href="https://www.altovita.com/privacy-policy/">Privacy Policy</a>
        .
      </p>
      <p className="disclaimer">
        Contact
        {' '}
        <a href={`mailto:${contacts.email}?subject=Problem with card payment`}>
          {contacts.email}
        </a>
        {' '}
        in case of problems.
      </p>
      <Form
        form={form}
        layout="vertical"
        onFinish={onSubmit}
        className="form"
        validateTrigger="onBlur"
        noValidate
      >
        <fieldset>
          <h3 className="sub-title">Personal info</h3>
          <Form.Item
            name="name"
            className="field field-bg"
            rules={[{ required: true, message: 'Full name is required' }]}
          >
            <Input bordered={false} type="text" className="form-item" placeholder="Full Name" />
          </Form.Item>
          <Form.Item
            name="email"
            className="field field-bg"
            rules={[
              { required: true, message: 'Email is required' },
              { type: 'email', message: 'Invalid email' },
            ]}
          >
            <Input bordered={false} type="email" className="form-item" placeholder="Email" />
          </Form.Item>
        </fieldset>
        <fieldset className="form__card">
          <h3 className="sub-title">Card info</h3>
          <div className="field field-bg">
            <CardNumberElement
              options={{
                ...STRIPE_ELEMENT_OPTIONS,
                showIcon: true,
                placeholder: 'Card Number',
              }}
              onChange={({ complete = false, error }) => {
                const errorMessage = getStripeErrorMessage(error);
                setCardNumberComplete(complete);
                setCardNumberError(errorMessage);
              }}
            />
            <ValidationMessage description={cardNumberError} />
          </div>
          <div className="field-sm-wrapper">
            <div className="field field-sm">
              <CardExpiryElement
                options={{
                  ...STRIPE_ELEMENT_OPTIONS,
                  placeholder: 'MM / YY',
                }}
                onChange={({ complete = false, error }) => {
                  const errorMessage = getStripeErrorMessage(error);
                  setCardExpiryComplete(complete);
                  setCardExpiryError(errorMessage);
                }}
              />
              <ValidationMessage description={cardExpiryError} />
            </div>
            <div className="field field-sm">
              <CardCvcElement
                options={{
                  ...STRIPE_ELEMENT_OPTIONS,
                  placeholder: 'CVV / CVC',
                }}
                onChange={({ complete = false, error }) => {
                  const errorMessage = getStripeErrorMessage(error);
                  setCardCvcComplete(complete);
                  setCardCvcError(errorMessage);
                }}
              />
              <ValidationMessage description={cardCvcError} />
            </div>
          </div>
        </fieldset>
        <div className="button-wrapper">
          {error && (
            <p className="error">
              {error}
            </p>
          )}
          <button
            type="submit"
            className="btn btn-submit"
            disabled={isSubmitButtonDisabled}
          >
            {isLoading ? 'Sending...' : 'Submit'}
          </button>
          {renderStripeBlock()}
        </div>
      </Form>
      <p className="note">
        AltoVita reserves the right to charge for any damages or incidental costs related to the booking,
        including but not limited to: additional services, overstay charges, use of telephone
        or Pay for View TV where applicable.
        <strong>You will always be notified before any charge is processed</strong>
      </p>
    </>
  );

  const renderSuccess = () => (
    <div className="success-window">
      <div>
        <div className="success-mark" />
        <h2>Succeeded</h2>
        <h3>
          Your payment details have been submitted.
          You will be notified, in case of any charge,
          related to additional services, overstay charges,
          use of telephone or Pay for View TV where applicable.
        </h3>
        {renderStripeBlock()}
      </div>
    </div>
  );

  return (
    <div className="card-form-wrapper">
      {
        isSucceeded ? renderSuccess() : renderForm()
      }
    </div>
  );
};

export default CardForm;
